import { MAIN_PRODUCT_URL } from '../env'

// Full-page navigation seam. jsdom makes window.location.assign non-writable
// and non-configurable, so tests mock this module instead of the global.
export function hardNavigate(url: string): void {
  window.location.assign(url)
}

// ---------------------------------------------------------------------------
// Dialer-only "Main Menu" hand-off
//
// The primary app can send users over with the path to return to, e.g.
// /dialer/human?back=/app/lists (a cross-origin referrer carries no path, so
// the link must say it). Captured once at boot, per tab, before the router
// exists — the SPA never manages the param.
//
// When that link was followed in this tab, the LeadIQ page is also the history
// entry directly behind us; its absolute index is recorded so "Main Menu" can
// traverse back to it (a bfcache restore) instead of reloading the whole app.
// ---------------------------------------------------------------------------

const BACK_PATH_KEY = 'raven:dialer:back-path'
const BACK_INDEX_KEY = 'raven:dialer:back-index'
const HISTORY_CAP = 50
// A traversal the browser ignores never fires beforeunload; after this long
// the URL is taken instead.
const TRAVERSAL_GRACE_MS = 1000

// Only a plain path is ever accepted, so appending it to MAIN_PRODUCT_URL can
// never change the host: any site can craft the param, but the worst it can
// pick is which PAGE of the primary app "Main Menu" opens — no more than any
// direct link to that app could. (Deliberately no referrer check: the legit
// flow bounces through the IdP on login, and hostile pages strip referrers
// anyway, so it would only break real users.)
function isValidBackPath(v: string): boolean {
  return (
    v.length <= 512 &&
    v.startsWith('/') &&
    !v.startsWith('//') &&
    !v.includes('\\') &&
    // eslint-disable-next-line no-control-regex
    !/[\u{0000}-\u{001f}\u{007f}]/u.test(v)
  )
}

function originOf(url: string): string {
  try {
    return new URL(url).origin
  } catch {
    return ''
  }
}

// Absolute session-history index of the current entry. Vue Router persists it
// per entry as state.position; a fresh navigation has no state yet but has
// truncated any forward entries, so length - 1 is exact there.
function currentIndexAtBoot(): number {
  const pos = window.history.state?.position
  return Number.isInteger(pos) ? pos : window.history.length - 1
}

function writeSession(key: string, value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(key)
    else window.sessionStorage.setItem(key, value)
  } catch {
    /* private mode — Main Menu falls back to the URL */
  }
}

export function captureBackPath(): void {
  let url: URL
  try {
    url = new URL(window.location.href)
  } catch {
    return
  }
  const referrer = originOf(document.referrer)
  const fromMainProduct = referrer !== '' && referrer === originOf(MAIN_PRODUCT_URL)
  const raw = url.searchParams.get('back')
  if (raw === null) {
    // A typed URL, bookmark or foreign link may sit in a tab whose history no
    // longer holds the recorded hand-off; reloads and Raven's own login
    // round-trip keep their referrer and their indices.
    if (!fromMainProduct && referrer !== window.location.origin) writeSession(BACK_INDEX_KEY, null)
    return
  }
  if (isValidBackPath(raw)) {
    writeSession(BACK_PATH_KEY, raw)
    const index = currentIndexAtBoot()
    // Only LeadIQ's own link leaves LeadIQ directly behind us; a new tab has
    // nothing behind (length 1), and a crafted ?back= must not gain a target.
    if (fromMainProduct && index >= 1) writeSession(BACK_INDEX_KEY, String(index - 1))
  }
  url.searchParams.delete('back')
  window.history.replaceState(window.history.state, '', url)
}

export function storedBackPath(): string | null {
  try {
    const v = window.sessionStorage.getItem(BACK_PATH_KEY)
    return v && isValidBackPath(v) ? v : null
  } catch {
    return null
  }
}

export function storedBackIndex(): number | null {
  try {
    const v = window.sessionStorage.getItem(BACK_INDEX_KEY)
    return v !== null && /^\d+$/.test(v) ? Number(v) : null
  } catch {
    return null
  }
}

// Traverses back to the recorded hand-off entry; false leaves the caller to
// navigate by URL.
export function returnToHandoffEntry(
  fallbackUrl: string,
  navigate: (url: string) => void = hardNavigate,
): boolean {
  const index = storedBackIndex()
  const pos = window.history.state?.position
  if (index === null || !Number.isInteger(pos)) return false
  if (window.history.length >= HISTORY_CAP) return false
  const delta = pos - index
  if (delta < 1) return false
  let started = false
  const onStart = () => { started = true }
  window.addEventListener('beforeunload', onStart, { once: true })
  window.history.go(-delta)
  window.setTimeout(() => {
    window.removeEventListener('beforeunload', onStart)
    if (!started) navigate(fallbackUrl)
  }, TRAVERSAL_GRACE_MS)
  return true
}
